/**
 * Renders one control from a scenario's Knob manifest. The manifest is data
 * the server declares (packages/server/src/engine/knobs.ts); this component
 * never hand maintains a parallel list of what each scenario supports; it
 * only knows how to draw the five kinds the manifest can contain. Adding a
 * knob to a scenario later, or opening up one of the other thirty one
 * scenarios, needs no change here.
 */

import type { ReactElement } from 'react';

import type { Knob } from '../../hooks/use-settings.ts';

export function KnobField({
  knob,
  value,
  onChange,
  disabled = false,
}: {
  knob: Knob;
  value: number | boolean | string | undefined;
  onChange: (value: number | boolean | string) => void;
  disabled?: boolean;
}): ReactElement {
  // Knob is a flat interface, not a discriminated union: min/max/step/options
  // are all optional regardless of kind, and value is number | boolean |
  // string on every variant. Narrowing has to happen at runtime with typeof
  // rather than falling out of the switch on knob.kind for free.
  switch (knob.kind) {
    case 'int':
    case 'ms':
    case 'rate': {
      const fallback = typeof knob.value === 'number' ? knob.value : 0;
      const current = typeof value === 'number' ? value : fallback;
      return (
        <div className="knob">
          <label className="knob__label" htmlFor={`knob-${knob.key}`}>
            {knob.label}
            {knob.unit !== undefined && <span className="muted"> ({knob.unit})</span>}
          </label>
          <div className="knob__control">
            <input
              id={`knob-${knob.key}`}
              type="range"
              className="range"
              min={knob.min}
              max={knob.max}
              step={knob.step}
              value={current}
              disabled={disabled}
              onChange={(event) => {
                onChange(Number(event.target.value));
              }}
            />
            <span className="mono knob__value">{current}</span>
          </div>
        </div>
      );
    }
    case 'bool': {
      const fallback = typeof knob.value === 'boolean' ? knob.value : false;
      const current = typeof value === 'boolean' ? value : fallback;
      return (
        <label className="switch knob knob--switch">
          <input
            type="checkbox"
            checked={current}
            disabled={disabled}
            onChange={(event) => {
              onChange(event.target.checked);
            }}
          />
          <span className="switch__track">
            <span className="switch__thumb" />
          </span>
          <span className="switch__label">{knob.label}</span>
        </label>
      );
    }
    case 'enum': {
      const fallback = typeof knob.value === 'string' ? knob.value : '';
      const current = typeof value === 'string' ? value : fallback;
      const options = knob.options ?? [];
      return (
        <div className="knob">
          <label className="knob__label" htmlFor={`knob-${knob.key}`}>
            {knob.label}
          </label>
          <select
            id={`knob-${knob.key}`}
            className="select"
            value={current}
            disabled={disabled}
            onChange={(event) => {
              onChange(event.target.value);
            }}
          >
            {options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      );
    }
  }
}
